import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Grid,
  useTheme,
  Fade,
  Container,
  Chip,
  Stack,
} from "@mui/material";
import {
  CalendarToday,
  LocationOn,
  People,
  AccessTime,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "universal-cookie";

export type ManagedShow = {
  id: number;
  name: string;
  date: string;
  closing_date: string;
  cap: number;
  venue: string;
};

export type EntryOverview = {
  id: number;
  account_id: number;
  show_id: number;
  reference: string;
  total_fees: string;
  paid: boolean;
  payload: {
    closing_date: string;
    description: string;
    email_address: string;
    entries: Array<{
      dog_name: string;
      handler_name?: string;
      handler_first_name?: string;
      handler_last_name?: string;
      grade: number;
      height: string;
      class_name?: string;
      class_details?: Array<{
        class_id: number;
        class_name: string;
        course_distance?: number;
        course_time?: number;
        height?: string;
      }>;
      kennel_club_name?: string;
      entry_type: number;
    }>;
    is_member: boolean;
    paid: boolean;
    reference: string;
    show_date: string;
    show_fees: string;
    show_id: number;
    show_name: string;
    total_owing: string;
  };
  created_at: string;
  email_address: string;
};

export type FlattenedEntry = {
  id: string;
  reference: string;
  email_address: string;
  dog_name: string;
  handler_name: string;
  grade: number;
  height: string;
  class_name: string;
  kennel_club_name: string;
  show_fees: string;
  paid: boolean;
  created_at: string;
  entryOverviewId?: number;
};

const ShowManagement = (): JSX.Element => {
  const [shows, setShows] = useState<ManagedShow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    getManagedShows();
  }, []);

  const getManagedShows = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = new Cookies().get("token");

      const response = await axios.get(
        "https://api.easyagility.co.uk/shows/managed",
        { headers: { Authorization: "Bearer " + token } }
      );

      setShows(response.data);
    } catch (err) {
      setError("Failed to load managed shows. Please try again later.");
      console.error("Error fetching managed shows:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    return date.toLocaleDateString("en-GB", options);
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getShowStatus = (showDate: string, closingDate: string) => {
    const now = new Date();
    const show = new Date(showDate);
    const closing = new Date(closingDate);

    if (closing < now) {
      return { status: "closed", label: "Closed", color: "default" as const };
    } else if (show < now) {
      return { status: "past", label: "Past", color: "default" as const };
    } else {
      return { status: "open", label: "Open", color: "success" as const };
    }
  };

  const handleShowClick = (show: ManagedShow) => {
    navigate(`/show-management/${show.id}/entries`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={600}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              mb: 4,
            }}
          >
            Show Management
          </Typography>

          {shows.length === 0 ? (
            <Alert severity="info">
              No managed shows found. Shows you manage will appear here.
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {shows?.map((show) => {
                const status = getShowStatus(show.date, show.closing_date);
                return (
                  <Grid item xs={12} md={6} key={show.id}>
                    <Fade in timeout={800}>
                      <Card
                        elevation={3}
                        onClick={() => handleShowClick(show)}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 2,
                          transition: "transform 0.2s, box-shadow 0.2s",
                          cursor: "pointer",
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 6,
                          },
                        }}
                      >
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Stack spacing={2}>
                            {/* Show Name and Status */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                  flex: 1,
                                }}
                              >
                                {show.name}
                              </Typography>
                              <Chip
                                label={status.label}
                                color={status.color}
                                size="small"
                              />
                            </Box>

                            {/* Show Date */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <CalendarToday
                                sx={{ color: theme.palette.text.secondary }}
                              />
                              <Typography variant="body1">
                                {formatDate(show.date)}
                              </Typography>
                            </Box>

                            {/* Closing Date */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <AccessTime
                                sx={{ color: theme.palette.text.secondary }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Closes: {formatDateTime(show.closing_date)}
                              </Typography>
                            </Box>

                            {/* Venue */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 1,
                              }}
                            >
                              <LocationOn
                                sx={{
                                  color: theme.palette.text.secondary,
                                  mt: 0.5,
                                }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {show.venue}
                              </Typography>
                            </Box>

                            {/* Capacity */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <People
                                sx={{ color: theme.palette.text.secondary }}
                              />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Capacity: {show.cap}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default ShowManagement;
